import { useState } from 'react'
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Lock, Mail, XCircle } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useToastStore } from '../store/toastStore'
import { login, fetchPermissions } from '../api/auth'
import { Button, Field, Input } from '../components/ui'
import { safeNext } from '../utils/loginRedirect'

/**
 * Přihlášení — mobil první: logo, dvě pole a velké tlačítko, vše v dosahu palce.
 * Po přihlášení „/" přesměruje na úvodní stránku role (viz HomeRedirect v App.tsx).
 */
export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const { isAuthenticated, setAuth } = useAuthStore()
  const { addToast } = useToastStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Po přihlášení zpět na stránku, ze které uživatele poslalo přihlášení (např. odkaz na doklad).
  const next = safeNext(searchParams.get('next'))

  if (isAuthenticated) {
    return <Navigate to={next ?? '/'} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setLoading(true)

    try {
      const response = await login(email, password)
      setAuth(response.user, response.tokens)
      await fetchPermissions()
      addToast('success', `Přihlášení úspěšné. Vítejte, ${response.user.name}!`)
      navigate(next ?? '/', { replace: true })
    } catch {
      setMessage({ type: 'error', text: 'Neplatné přihlašovací údaje. Zkontrolujte e-mail a heslo.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="pt-safe pb-safe flex min-h-app flex-col bg-slate-50">
      {/* Jemný červený akcent nahoře — značka bez křiklavých ploch */}
      <div className="h-1 w-full bg-brand-500" aria-hidden />
      <div className="flex flex-1 items-start justify-center px-4 py-10 sm:items-center sm:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center">
            <img src="/das-logo.png" alt="DaS elektromontáže" className="h-16 w-auto sm:h-20" />
            <h1 className="mt-6 text-xl font-bold tracking-tight text-ink-900">Přihlášení do DaS Flow</h1>
            <p className="mt-1 text-sm text-ink-500">Docházka, zakázky, přípravy a nářadí na jednom místě</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {message && (
                <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                  <XCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{message.text}</span>
                </div>
              )}

              <Field label="E-mail" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  leading={<Mail size={18} />}
                  placeholder="jmeno@firma.cz"
                />
              </Field>

              <Field label="Heslo" htmlFor="password">
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    leading={<Lock size={18} />}
                    className="pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                    aria-pressed={showPassword}
                    className="absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center rounded-r-lg text-ink-500 hover:text-ink-800"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </Field>

              <Button type="submit" size="lg" loading={loading} block>
                Přihlásit se
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
